import { AKTION_10, FEVorvertragsnummer, FeatureToggles, Gefahr, TIER_10 } from '@ps20-frontend/frontend-models';
import { Feature, Produktlinienname, Zahlungsart, Zahlweise } from '@ps20/bff-models';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import {
  PartialProduktlinieNew,
  StateExtremwetterschutzNew,
  StateExtremwetterschutzPre,
  StateProdukt,
  StateProduktNew,
  StateProduktPreProduktlinie,
  StateProduktVorteilsRabatt,
  StateVertragsoptionen,
  StateVertragsoptionenPre,
  StateVertragsoptionenSpLaufnummerNew,
  StateVertragsoptionenSpLaufnummerPre,
} from './globalMigrators.interface';

const VORTEILS_RABATT_KONZEPT_MIN_PREISVERZEICHNIS_VALUE = 30;
export const ANGEBOTSDATUM_RESET_PERIOD_IN_DAYS = 21;

/**
 * Calculates the appropriate angebotsdatum value
 * Returns 2025-08-01 if current date is before that date, otherwise returns current date
 */
const getMinimumAngebotsdatum = (): string => {
  const currentDate = dayjs().format(DATEFORMAT_SHORT_ISO);
  const minDate = '2025-08-01';

  return dayjs(currentDate).isBefore(minDate) ? minDate : currentDate;
};

export const fevorvertragsnummer = (
  previousState: StateVertragsoptionenSpLaufnummerPre,
  newVersion: number,
): StateVertragsoptionenSpLaufnummerNew => {
  const vertragsoption = previousState.vertragsoptionen;
  let newVertragsoption;
  if (vertragsoption.vorvertragsNummern) {
    const newVorvertragsnummern = vertragsoption.vorvertragsNummern.map(vertragnummer => {
      return { spLaufnummer: null, vertragsnummer: vertragnummer } as FEVorvertragsnummer;
    });
    newVertragsoption = {
      ...vertragsoption,
      vorvertragsNummern: newVorvertragsnummern,
    };
  } else {
    newVertragsoption = {
      ...vertragsoption,
      vorvertragsNummern: undefined,
    };
  }
  return {
    ...previousState,
    version: newVersion,
    vertragsoptionen: newVertragsoption,
  };
};

export const generateMigrateRisikoschluessel = (
  gefahrenTransformer: (g: Gefahr) => Gefahr,
): ((s: StateProdukt, n: number) => StateProdukt) => {
  return (previousState: StateProdukt, newVersion: number): StateProdukt => {
    if (!previousState.produkt) {
      return { ...previousState, version: 3 };
    }
    const produktlinien = previousState.produkt.produktlinien.map(linie => {
      return {
        ...linie,
        gefahren: linie.gefahren.map(gefahr => gefahrenTransformer(gefahr)),
      };
    });
    return {
      ...previousState,
      produkt: { ...previousState.produkt, produktlinien },
      version: newVersion,
    };
  };
};

export const migrateZahlungsoptionen = (
  previousState: StateVertragsoptionenPre,
  newVersion: number,
): StateVertragsoptionen => {
  return {
    ...previousState,
    version: newVersion,
    vertragsoptionen: {
      ...previousState.vertragsoptionen,
      zahlungsweise: previousState.vertragsoptionen.zahlungsweise
        ? getZahlweiseFromText(previousState.vertragsoptionen.zahlungsweise)
        : undefined,
      zahlungsart: previousState.vertragsoptionen.zahlungsart
        ? (Zahlungsart as Record<string, Zahlungsart>)[previousState.vertragsoptionen.zahlungsart.toUpperCase()]
        : undefined,
    },
  };
};

export const migrateProduktnameToEnum = (
  previousState: StateProduktPreProduktlinie,
  newVersion: number,
): StateProduktNew => {
  const newProduktlinien: PartialProduktlinieNew[] = previousState.produkt?.produktlinien?.map(
    linie => {
      return {
        ...linie,
        produktName: (Produktlinienname as Record<string, Produktlinienname>)[linie?.produktName?.toUpperCase()],
      };
    },
  );
  return {
    ...previousState,
    version: newVersion,
    produkt: {
      ...previousState.produkt,
      produktlinien: newProduktlinien,
    },
    savedGesamtbeitragMitRabatt: previousState.gesamtbeitragMitRabatt,
    savedJahresbeitragMitRabatt: previousState.jahresbeitrag,
    taasInfo: {
      ...previousState.taasInfo,
      savedTbNachlass: previousState.taasInfo.tbNachlass,
      savedRbdNachlass: previousState.taasInfo.rbdNachlass,
    },
  };
};

export function getZahlweiseFromText(text: string): Zahlweise {
  return (Zahlweise as Record<string, Zahlweise>)[
    text.toLocaleUpperCase().replace('Ä', 'AE').replace('Ü', 'UE').replace('Ö', 'OE')
  ];
}

export const migrateExtremwetterschutz = (
  previousState: StateExtremwetterschutzPre,
  newVersion: number,
): StateExtremwetterschutzNew => {
  const extremwetterschutzValue = {
    text: 'Für das Haus bzw. Grundstück wurde folgende Risikostufe für Hochwasser und Starkregen ermittelt:',
    info: 'Die Risikobewertung beruht auf rein statistischen Auswertungen. Verbindliche Aussagen zu zukünftigen Extremwetterereignissen und Naturkatastrophen kann die Risikobewertung nicht treffen.',
    durchschnittlich: 'Durchschnittliches Risiko',
    erhoeht: 'Erhöhtes Risiko',
    gravierend: 'Gravierendes Risiko',
  };

  const updatedProduktlinien = previousState.produkt?.produktlinien?.map(produktlinie => {
    const updatedZusatzoptionen = produktlinie.zusatzoptionen.map(zusatzoption => {
      if (zusatzoption.id === 'weitereNaturgefahren') {
        return {
          ...zusatzoption,
          extremwetterschutz: extremwetterschutzValue,
        };
      }
      return zusatzoption;
    });

    return {
      ...produktlinie,
      zusatzoptionen: updatedZusatzoptionen,
    };
  });

  return {
    ...previousState,
    version: newVersion,
    produkt: {
      ...previousState.produkt,
      produktlinien: updatedProduktlinien,
    },
  };
};

export const migrateProduktForVorteilsrabatt = (
  previousState: StateProduktVorteilsRabatt,
  newVersion: number,
  currentState: StateProduktVorteilsRabatt,
  beibehalten: boolean,
): StateProduktVorteilsRabatt => {
  if (previousState.produkt && !beibehalten) {
    previousState.taasInfo.rbdNachlass = 0;
    previousState.taasInfo.tbNachlass = 0;
    delete previousState.taasInfo.savedRbdNachlass;
    delete previousState.taasInfo.savedTbNachlass;
    previousState.produkt?.produktlinien?.forEach(produktLinie => {
      if (produktLinie.preisverzeichnis < VORTEILS_RABATT_KONZEPT_MIN_PREISVERZEICHNIS_VALUE) {
        delete produktLinie.preisverzeichnis;
      }
    });
  }
  previousState.vertragsoptionen.angebotsdatum = beibehalten
    ? '1900-01-01'
    : getMinimumAngebotsdatum();
  return {
    ...previousState,
    version: newVersion,
  };
};

export const migrateProduktForVorteilsRabattFtActive = (loadedAndMigratedState: StateProduktVorteilsRabatt): void => {
  const isFtVorteilsRabatt =
    loadedAndMigratedState.transientState.featureToggles[Feature.VORTEILSRABATT];
  if (isFtVorteilsRabatt) {
    loadedAndMigratedState.produkt?.produktlinien?.forEach(produktLinie => {
      if (produktLinie.preisverzeichnis < VORTEILS_RABATT_KONZEPT_MIN_PREISVERZEICHNIS_VALUE) {
        if (!loadedAndMigratedState.vertragsoptionen.beibehalten) {
          loadedAndMigratedState.taasInfo.rbdNachlass = 0;
          loadedAndMigratedState.taasInfo.tbNachlass = 0;
          delete loadedAndMigratedState.taasInfo.savedRbdNachlass;
          delete loadedAndMigratedState.taasInfo.savedTbNachlass;
          delete produktLinie.preisverzeichnis;
        }
        loadedAndMigratedState.vertragsoptionen.angebotsdatum = loadedAndMigratedState
          .vertragsoptionen.beibehalten
          ? '1900-01-01'
          : loadedAndMigratedState.vertragsoptionen.angebotsdatum || getMinimumAngebotsdatum();
      } else {
        loadedAndMigratedState.vertragsoptionen.angebotsdatum =
          loadedAndMigratedState.vertragsoptionen.angebotsdatum || getMinimumAngebotsdatum();
      }
    });
  }
};

export const migrateAktionsnachlassAktion10 = (
  loadedAndMigratedState: Record<string, unknown>,
  featureToggles: FeatureToggles,
): void => {
  const isAktionsnachlassFtAktivated = featureToggles[Feature.AKTION_10];
  const vertragsoptionen = loadedAndMigratedState['vertragsoptionen'] as Record<string, unknown> | undefined;
  const selectedKooperation = vertragsoptionen?.['selectedKooperation'] as Record<string, unknown> | undefined;
  if (selectedKooperation?.['code'] === AKTION_10 && !isAktionsnachlassFtAktivated) {
    delete vertragsoptionen?.['selectedKooperation'];
  }
};

export const migrateAktionsnachlassTier10 = (
  loadedAndMigratedState: Record<string, unknown>,
  featureToggles: FeatureToggles,
): void => {
  const isTier10FtAktivated = featureToggles[Feature.TIER_10];
  const vertragsoptionen = loadedAndMigratedState['vertragsoptionen'] as Record<string, unknown> | undefined;
  const selectedKooperation = vertragsoptionen?.['selectedKooperation'] as Record<string, unknown> | undefined;
  if (selectedKooperation?.['code'] === TIER_10 && !isTier10FtAktivated) {
    delete vertragsoptionen?.['selectedKooperation'];
  }
};

export interface AngebotsdatumResetItem {
  beibehalten?: boolean;
  angebotsdatum?: string;
  versichertesRisiko?: string;
}

export interface AngebotsdatumResetLogEntry {
  versichertesRisiko?: string;
  oldDate: string;
  newDate: string;
}

export const migrateAngebotsdatumArray = <T extends AngebotsdatumResetItem>(
  items: Array<T>,
  options: { enableLogging?: boolean } = {}
): AngebotsdatumResetLogEntry[] => {
  const logEntries: AngebotsdatumResetLogEntry[] = [];

  items
    .filter(item => !item.beibehalten && item.angebotsdatum)
    .forEach(item => {
      const today = dayjs();
      const angebotDate = dayjs(item.angebotsdatum, DATEFORMAT_SHORT_ISO);
      const daysDifference = today.diff(angebotDate, 'day');

      if (daysDifference > ANGEBOTSDATUM_RESET_PERIOD_IN_DAYS) {
        const oldDate = angebotDate.format(DATEFORMAT_LOCAL_STATE);
        const newDate = today.format(DATEFORMAT_SHORT_ISO);
        const newDateFormatted = today.format(DATEFORMAT_LOCAL_STATE);

        item.angebotsdatum = newDate;

        if (options.enableLogging) {
          logEntries.push({
            versichertesRisiko: item.versichertesRisiko,
            oldDate,
            newDate: newDateFormatted,
          });
        }
      }
    });

  return logEntries;
};

export const migrateAngebotsdatum = (loadedAndMigratedState: { vertragsoptionen?: { beibehalten?: boolean; angebotsdatum?: string } }): void => {
  const angebotsdatum = loadedAndMigratedState.vertragsoptionen?.angebotsdatum;

  if (!loadedAndMigratedState.vertragsoptionen?.beibehalten && angebotsdatum) {
    const today = dayjs();
    const angebotDate = dayjs(angebotsdatum, DATEFORMAT_SHORT_ISO);
    const daysDifference = today.diff(angebotDate, 'day');

    if (daysDifference > ANGEBOTSDATUM_RESET_PERIOD_IN_DAYS) {
      loadedAndMigratedState.vertragsoptionen.angebotsdatum = today.format(DATEFORMAT_SHORT_ISO);
    }
  }
};
